import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { NotificationProvider, useNotifications } from './context/NotificationContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { ToastProvider } from './context/ToastContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import Sidebar from './components/Sidebar';
import GlobalSearch from './components/GlobalSearch';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import Services from './pages/Services';
import Tasks from './pages/Tasks';
import Clients from './pages/Clients';
import Team from './pages/Team';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import Finance from './pages/Finance';
import Quotations from './pages/Quotations';
import Leads from './pages/Leads';
import EmbeddedForms from './pages/EmbeddedForms';
import PublicLeadForm from './pages/PublicLeadForm';
import Drive from './pages/Drive';
import Settings from './pages/Settings';
import AuditLog from './pages/AuditLog';
import ClientProfile from './pages/ClientProfile';
import ClientContract from './pages/ClientContract';
import './styles/global.css';

const TopBar = ({ title }) => {
  const { theme, toggle } = useTheme();
  const { unreadCount } = useNotifications();
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  return (
    <div className="topbar">
      <div className="page-title">{title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <GlobalSearch />
        <button className="theme-toggle" onClick={toggleLanguage} title="Language">
          {language === 'ar' ? 'EN' : 'AR'}
        </button>
        <button className="notif-bell" onClick={() => navigate('/notifications')} title="Notifications">
          Bell
          {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
        <button className="theme-toggle" onClick={toggle} title="Toggle theme">
          {theme === 'dark' ? t('light') : t('dark')}
        </button>
      </div>
    </div>
  );
};

const Layout = ({ children, title }) => (
  <div className="app-layout">
    <Sidebar />
    <div className="main-content"><TopBar title={title} />{children}</div>
  </div>
);

const PrivateRoute = ({ children, roles, permKey }) => {
  const { user, loading } = useAuth();
  const { settings } = useSettings();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#666' }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    // Check if user has an explicit permission grant for this page
    if (permKey) {
      try {
        const perms = JSON.parse(settings.permissions || '{}');
        if (perms?.[user.role]?.[permKey]) return children;
      } catch (_) {}
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/forms/lead" element={<PublicLeadForm />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/dashboard" element={<PrivateRoute><Layout title="Dashboard"><Dashboard /></Layout></PrivateRoute>} />
      <Route path="/contract" element={<PrivateRoute roles={['client']}><Layout title="Services"><ClientContract /></Layout></PrivateRoute>} />
      <Route path="/projects" element={<PrivateRoute roles={['admin','manager']}><Layout title="Projects"><Projects /></Layout></PrivateRoute>} />
      <Route path="/projects/:id" element={<PrivateRoute roles={['admin','manager']}><Layout title="Project Detail"><ProjectDetail /></Layout></PrivateRoute>} />
      <Route path="/services" element={<PrivateRoute roles={['admin','manager']} permKey="services"><Layout title="Services"><Services /></Layout></PrivateRoute>} />
      <Route path="/tasks" element={<PrivateRoute roles={['admin','manager','employee','client']}><Layout title="Tasks"><Tasks /></Layout></PrivateRoute>} />
      <Route path="/clients" element={<PrivateRoute roles={['admin','manager']} permKey="clients"><Layout title="Clients"><Clients /></Layout></PrivateRoute>} />
      <Route path="/clients/:id" element={<PrivateRoute roles={['admin','manager']} permKey="clients"><Layout title="Client Profile"><ClientProfile /></Layout></PrivateRoute>} />
      <Route path="/leads" element={<PrivateRoute roles={['admin','manager']}><Layout title="Leads"><Leads /></Layout></PrivateRoute>} />
      <Route path="/embedded-forms" element={<PrivateRoute roles={['admin','manager']}><Layout title="Embedded Forms"><EmbeddedForms /></Layout></PrivateRoute>} />
      <Route path="/team" element={<PrivateRoute roles={['admin','manager']}><Layout title="Team"><Team /></Layout></PrivateRoute>} />
      <Route path="/profile" element={<PrivateRoute><Layout title="Profile"><Profile /></Layout></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute><Layout title="Notifications"><Notifications /></Layout></PrivateRoute>} />
      <Route path="/quotations" element={<PrivateRoute roles={['admin','manager','client']} permKey="quotations"><Layout title="Quotations"><Quotations /></Layout></PrivateRoute>} />
      <Route path="/finance" element={<PrivateRoute roles={['admin','manager','client']} permKey="invoices"><Layout title="Finance"><Finance /></Layout></PrivateRoute>} />
      <Route path="/drive" element={<PrivateRoute roles={['admin','manager','client']} permKey="files"><Layout title="Drive"><Drive /></Layout></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute roles={['admin']}><Layout title="Settings"><Settings /></Layout></PrivateRoute>} />
      <Route path="/audit-log" element={<PrivateRoute roles={['admin']}><Layout title="Audit Log"><AuditLog /></Layout></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App = () => (
  <ThemeProvider>
    <AuthProvider>
      <SettingsProvider>
        <NotificationProvider>
          <LanguageProvider>
            <ToastProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </ToastProvider>
          </LanguageProvider>
        </NotificationProvider>
      </SettingsProvider>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
